import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "multiple-websites-with-upward"
    }}>{`Multiple websites with UPWARD`}</h1>
    <p>{`This topic describes how to configure your UPWARD server to run multiple PWA sites from a single Magento Open Source or Adobe Commerce backend.`}</p>
    <h2 {...{
      "id": "the-upward-connector-module"
    }}>{`The UPWARD connector module`}</h2>
    <p>{`When you use the PWA scaffolding tool to create a PWA Studio project, one of the many modules installed is the UPWARD PHP connector module—`}<inlineCode parentName="p">{`magento2-upward-connector`}</inlineCode>{`. This module provides a CLI to help you configure different PWA sites to run from a single Commerce or Open Source server.`}</p>
    <h2 {...{
      "id": "configuring-the-upward-server"
    }}>{`Configuring the UPWARD server`}</h2>
    <p>{`The first step is to configure the UPWARD server to access the different PWA sites you want to deploy. And the CLI provided by the UPWARD module is how you do it.`}</p>
    <p>{`But before we get to the specifics, here's the overview of what you are actually doing. When you execute the CLI commands, you're actually adding `}<inlineCode parentName="p">{`pwa_paths`}</inlineCode>{` to the `}<inlineCode parentName="p">{`env.php`}</inlineCode>{` file. The commands specify the paths to the `}<inlineCode parentName="p">{`upward.yml`}</inlineCode>{` configuration for each PWA site you want to deploy. The following  example shows what an `}<inlineCode parentName="p">{`env.php`}</inlineCode>{` configuration file may look like AFTER using the CLI to add two different PWA sites to the `}<inlineCode parentName="p">{`default`}</inlineCode>{` and `}<inlineCode parentName="p">{`website`}</inlineCode>{` configurations:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`// ...
'downloadable_domains' => [
    // ...
],
# New configuration point
'pwa_path' => [
    'default' => [
        'default' => '/var/www/html/my-pwa-site/dist/upward.yml'
    ],
    'website' => [
        '<website_code>' => '/var/www/html/my-awesome-pwa-site/dist/upward.yml'
    ],
    'store' => [
        '<store_code>' => '' # Setting an empty string (or \`false\` value) will serve the specified default site (my-pwa-site)
    ]
]
`}</code></pre>
    <h2 {...{
      "id": "using-upward-cli-commands"
    }}>{`Using UPWARD CLI commands`}</h2>
    <p>{`The UPWARD module commands provide a convenient way to configure the server to deploy different PWA sites. The following examples show common usage patterns.`}</p>
    <p><strong parentName="p">{`Set UPWARD paths with `}<inlineCode parentName="strong">{`scopeType`}</inlineCode>{` and `}<inlineCode parentName="strong">{`scopeCode`}</inlineCode>{`:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`bin/magento pwa:upward:set --path /var/www/html/pwa/dist/upward.yml --scopeType store --scopeCode <store_code>
`}</code></pre>
    <p>{`To find the paths of your PWA sites (by scope) as shown above for the store scope, use one of the following commands:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`bin/magento store:list`}</inlineCode>{` (for store scope)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`bin/magento store:website:list`}</inlineCode>{` (for store and website scopes)`}</li>
    </ul>
    <p>{`Paths to `}<inlineCode parentName="p">{`upward.yml`}</inlineCode>{` can be relative: `}<inlineCode parentName="p">{`pwa/dist/upward.yml`}</inlineCode>{` or absolute: `}<inlineCode parentName="p">{`/var/www/html/pwa/dist/upward.yml`}</inlineCode>{`.`}</p>
    <p>{`To serve the default store front (non-PWA), use an empty string:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`bin/magento pwa:upward:set
`}</code></pre>
    <p>{`The UPWARD path configurations work the same as other store configurations: The config settings fall up in scope from `}<inlineCode parentName="p">{`store`}</inlineCode>{` > `}<inlineCode parentName="p">{`website`}</inlineCode>{` > `}<inlineCode parentName="p">{`global`}</inlineCode>{` (default), trying to serve the most specific, available site scope first.`}</p>
    <h2 {...{
      "id": "creating-multiple-bundles"
    }}>{`Creating multiple bundles`}</h2>
    <p>{`You can also generate unique bundles per website within a single scaffolded project.`}</p>
    <p>{`For example, if you want to deploy both French and German versions of your site, you could use the following commands to configure your UPWARD server:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`MAGENTO_BACKEND_URL=https://french.mysite.mg yarn build
mv dist dist-french
MAGENTO_BACKEND_URL=https://german.mysite.mg yarn build
mv dist dist-german
 
# copy these bundles to your Magento root and set configuration variables
bin/magento pwa:upward:set --path ./dist-french/upward.yml --scopeType website --scopeCode fr
bin/magento pwa:upward:set --path ./dist-german/upward.yml --scopeType website --scopeCode de
`}</code></pre>
    <p>{`With this configuration, a single UPWARD server can process requests for many different website bundles.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      